/* eslint-disable @typescript-eslint/no-explicit-any */
import { Text, ReactTable, Box, Link, VStack } from '@wegroup/design-system';
import { format } from 'date-fns';
import { isNaN } from 'lodash';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Cell } from 'react-table';
import { toEuro } from '../../../../../../sales-conversation/src/utils/stringUtils';
import { Claim } from '../../../../types/CompanyScanTypes';
import InfoPopover from '../common/InfoPopover';
import TableActionsMenu from '../common/TableActionsMenu';
import TableFiller from '../common/TableFiller';

interface Props {
  claims: Claim[];
  onEdit: (claim: Claim, index: number) => void;
  onRemove: (index: number) => void;
}

const ClaimsTable: React.FC<React.PropsWithChildren<Props>> = ({
  claims,
  onEdit,
  onRemove,
}) => {
  const { t } = useTranslation();

  const renderTitleCell = (cell: Cell) => {
    return (
      <Text color="text.gray">
        {t('CLAIM')} {cell.row.index + 1}
      </Text>
    );
  };

  const renderDateCell = (cell: Cell) => {
    const { claim_date } = cell?.row?.original as Claim;

    const formattedDate = format(new Date(claim_date), `yyyy-MM-dd`);
    return <Text color="text.gray">{formattedDate}</Text>;
  };

  const renderExpensesCell = (cell: Cell) => {
    const { expenses } = cell?.row?.original as Claim;

    return (
      <Text color="text.gray">
        {/* `any` usage for this stupid bug, please remove when there is time */}
        {!isNaN(parseInt(expenses as any)) ? toEuro(expenses) : '-'}
      </Text>
    );
  };

  const renderhas_certificateCell = (cell: Cell) => {
    const { has_certificate } = cell?.row?.original as Claim;

    return <Text color="text.gray">{has_certificate ? 'Yes' : 'No'}</Text>;
  };

  const renderActionMenuCell = (cell: Cell) => {
    const claim = cell?.row?.original as Claim;
    const index = cell?.row?.index;

    return (
      <TableActionsMenu
        onEdit={() => onEdit(claim, index)}
        onRemove={() => onRemove(index)}
      />
    );
  };

  const renderCircumstancesCell = (cell: Cell) => {
    const { circumstances } = cell?.row?.original as Claim;

    if (!circumstances) return '-';

    return (
      <InfoPopover>
        <Text>{circumstances}</Text>
      </InfoPopover>
    );
  };

  const renderDocumentsCell = (cell: Cell) => {
    const { documents } = cell?.row?.original as Claim;
    const index = cell?.row?.index;

    if (!documents) return '-';

    return (
      <InfoPopover text={t('VIEW')}>
        <VStack spacing="2" align="stretch">
          {documents.map((document) => (
            <Link
              variant="gray"
              key={document.location}
              href={document.public_bucket_link}
              isExternal
            >
              {t('DOCUMENT')} {index + 1}: {document.filename}
            </Link>
          ))}
        </VStack>
      </InfoPopover>
    );
  };

  const columns = useMemo(
    () => [
      {
        accessor: 'number',
        Cell: renderTitleCell,
        disableSortBy: true,
        isSticky: true,
      },
      {
        Header: t('Date'),
        accessor: 'claimDate',
        Cell: renderDateCell,
      },
      {
        Header: t('EXPENSES'),
        accessor: 'expenses',
        Cell: renderExpensesCell,
      },
      {
        Header: t('HAS_CERTIFICATE'),
        accessor: 'has_certificate',
        Cell: renderhas_certificateCell,
      },
      {
        Header: t('CIRCUMSTANCES'),
        accessor: 'circumstances',
        Cell: renderCircumstancesCell,
        disableSortBy: true,
      },
      {
        Header: t('DOCUMENTS'),
        accessor: 'documents',
        Cell: renderDocumentsCell,
        disableSortBy: true,
      },
      {
        Header: t('ACTIONS'),
        id: 'actions',
        Cell: renderActionMenuCell,
        disableSortBy: true,
      },
    ],
    [claims],
  );

  return (
    <Box>
      <ReactTable data={claims} columns={columns as any} />
      {claims.length === 0 && (
        <TableFiller>
          <Text>{t('FLOWS_COMPANY-SCAN_CLAIMS_EMPTY-TABLE')}</Text>
        </TableFiller>
      )}
    </Box>
  );
};

export default ClaimsTable;
